import React, { useCallback, useEffect, useState, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    CircularProgress,
    Alert
} from "@mui/material";
import {
    DmsPage,
    DmsAltBox,
    intl,
    fetchData,
    HTTP_STATUS,
    DmsTabber,
    Notifications,
    LanguageBaseContext,
} from "dmsbasereact";
import { ProfileInformationTab } from "./tabs/ProfileInformationTab";
import { ChangePasswordTab } from "./tabs/ChangePasswordTab";
import { SecurityTab } from "./tabs/SecurityTab";
import { InstitutionTab } from "./tabs/InstitutionTab";
import { DocumentsTab } from "./tabs/DocumentsTab";
import { UserTypesTab } from "./tabs/UserTypesTab";
import { getUserTypeLabel, mapBackendTypeToFrontend } from "../../constants/UserType";
import { TOTPSetupModal } from "./components/TOTPSetupModal";

export const ProfileSettingsPage = () => {
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const { language: systemLanguage } = useContext(LanguageBaseContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState(null);
    const [error, setError] = useState(null);
    const [userType, setUserType] = useState(null);
    const [userTypes, setUserTypes] = useState([]);
    const [mfaMethods, setMfaMethods] = useState([]);
    const [loadingMfa, setLoadingMfa] = useState(false);
    const [totpSetupModal, setTotpSetupModal] = useState({ open: false, otpauthUrl: null });
    const [documentsTabKey, setDocumentsTabKey] = useState(0);

    // Fetch current user profile
    const fetchProfile = useCallback(async () => {
        try {
            const response = await fetchData("/person/my_profile", "GET", true);

            if (response.status === HTTP_STATUS.OK_200 && response.data) {
                const profileData = response.data.data || response.data;
                setProfileData(profileData);

                const userType = profileData.user_type || "regular_user";
                setUserType(userType);
                setUserTypes(profileData.user_types || []);

                // Fetch MFA methods for all users with person profile
                const hasPerson = profileData.person || false;
                if (hasPerson || userType === 'person' || userType === 'doctor' || userType === 'pharmacist' || userType === 'institution_responsible' || userType === 'distributor_responsible') {
                    setLoadingMfa(true);
                    fetchMfaMethods();
                }
            } else {
                setError(response.detail || "Failed to load profile data");
            }
        } catch (err) {
            console.error("Error fetching profile:", err);
            setError("Error loading profile data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const fetchMfaMethods = async () => {
        try {
            const response = await fetchData("/person/my_profile/mfa", "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                // Handle nested data structure: response.data.data or response.data
                const responseData = response.data?.data || response.data;
                const methods = Array.isArray(responseData) ? responseData : [];
                setMfaMethods(methods);
            } else {
                setMfaMethods([]);
            }
        } catch (err) {
            console.error("Error fetching MFA methods:", err);
            setMfaMethods([]);
        } finally {
            setLoadingMfa(false);
        }
    };

    const handlePasswordChange = async (passwordData) => {
        try {
            const response = await fetchData("/person/my_profile/password", "PUT", true, passwordData);

            // Check for specific error messages from backend
            if (response.status === HTTP_STATUS.BAD_REQUEST_400 || response.status === HTTP_STATUS.UNAUTHORIZED_401) {
                let errorMessage = response.data?.detail || response.detail || intl.get("PasswordChangeFailed").d("Failed to change password");

                // Translate specific backend error messages
                if (errorMessage.includes("New password cannot be the same as current password")) {
                    errorMessage = intl.get("NewPasswordSameAsCurrent").d("New password cannot be the same as current password");
                } else if (errorMessage.includes("Current password is incorrect")) {
                    errorMessage = intl.get("CurrentPasswordIncorrect").d("Current password is incorrect");
                } else if (errorMessage.includes("do not match")) {
                    errorMessage = intl.get("PasswordsDoNotMatch").d("Passwords do not match");
                } else if (errorMessage.includes("does not meet requirements")) {
                    errorMessage = intl.get("PasswordDoesNotMeetRequirements").d("Password does not meet security requirements");
                }

                // Display specific error message and prevent DmsForm from showing generic error
                Notifications.toast(errorMessage, Notifications.TYPE_ERROR);

                // Return a modified response to prevent DmsForm from showing another toast
                return { ...response, status: HTTP_STATUS.OK_200 };
            }

            Notifications.toast(intl.get("Savedsuccessfully").d("Saved successfully!"), Notifications.TYPE_SUCCESS);
            return { success: true };
        } catch (err) {
            console.error("Error changing password:", err);
            let errorMessage = err.response?.data?.detail || intl.get("PasswordChangeFailed").d("Failed to change password");

            // Translate specific backend error messages in catch block
            if (typeof errorMessage === 'string') {
                if (errorMessage.includes("New password cannot be the same as current password")) {
                    errorMessage = intl.get("NewPasswordSameAsCurrent").d("New password cannot be the same as current password");
                } else if (errorMessage.includes("Current password is incorrect")) {
                    errorMessage = intl.get("CurrentPasswordIncorrect").d("Current password is incorrect");
                }
            }

            // Display specific error message
            Notifications.toast(errorMessage, Notifications.TYPE_ERROR);

            // Return OK status to prevent DmsForm from showing generic error
            return { status: HTTP_STATUS.OK_200 };
        }
    };

    const handleAddMfa = async (mfaType) => {
        setLoadingMfa(true);
        try {
            let res;
            switch (mfaType) {
                case "EMAIL":
                    res = await fetchData("/person/my_profile/mfa/email", "POST", true);
                    if (res.status === HTTP_STATUS.CREATED_201 || res.status === HTTP_STATUS.OK_200) {
                        Notifications.toast(
                            intl.get("EmailMFAAddedSuccess").d("Email authentication added successfully"),
                            Notifications.TYPE_SUCCESS
                        );
                        await fetchMfaMethods();
                    } else if (res.status === HTTP_STATUS.CONFLICT_409) {
                        Notifications.toast(
                            intl.get("EmailMFAAlreadyConfigured").d("Email authentication is already configured"),
                            Notifications.TYPE_WARNING
                        );
                    } else {
                        Notifications.toast(
                            res.data?.detail || intl.get("MFAAddFailed").d("Failed to add authentication method"),
                            Notifications.TYPE_ERROR
                        );
                    }
                    break;
                case "TOTP":
                    res = await fetchData("/mfa/totp/generate_secret_key_by_user_pk", "POST", true);
                    if (res.status === HTTP_STATUS.CREATED_201 || res.status === HTTP_STATUS.OK_200) {
                        // API returns: { detail: ..., data: { otpauth_url: uri } }
                        const otpauthUrl = res.data?.data?.otpauth_url || res.data?.otpauth_url;
                        if (otpauthUrl) {
                            setTotpSetupModal({ open: true, otpauthUrl });
                        } else {
                            console.error("[MFA] TOTP response missing otpauth_url:", res);
                            Notifications.toast(
                                intl.get("TOTPSetupError").d("Error starting authenticator setup. Please try again."),
                                Notifications.TYPE_ERROR
                            );
                        }
                    } else if (res.status === HTTP_STATUS.CONFLICT_409) {
                        Notifications.toast(
                            intl.get("TOTPAlreadyConfigured").d("Authenticator app is already configured"),
                            Notifications.TYPE_WARNING
                        );
                    } else {
                        Notifications.toast(
                            res.data?.detail || intl.get("MFAAddFailed").d("Failed to add authentication method"),
                            Notifications.TYPE_ERROR
                        );
                    }
                    break;
                case "SMS":
                case "WHATSAPP":
                    Notifications.toast(
                        intl.get("MFAMethodNotSupported").d("This authentication method is not yet supported"),
                        Notifications.TYPE_WARNING
                    );
                    setLoadingMfa(false);
                    return { status: HTTP_STATUS.BAD_REQUEST_400, detail: `MFA type ${mfaType} is not currently supported.` };
                default:
                    console.error("Unknown MFA type:", mfaType);
                    Notifications.toast(
                        intl.get("UnknownMFAType").d("Unknown authentication method"),
                        Notifications.TYPE_ERROR
                    );
                    setLoadingMfa(false);
                    return { status: HTTP_STATUS.BAD_REQUEST_400, detail: "Unknown MFA type" };
            }
            return res;
        } catch (err) {
            console.error("Error adding MFA:", err);
            Notifications.toast(
                intl.get("MFAAddError").d("Error adding authentication method. Please try again."),
                Notifications.TYPE_ERROR
            );
            return { status: HTTP_STATUS.INTERNAL_SERVER_ERROR_500 };
        } finally {
            setLoadingMfa(false);
        }
    };

    const handleTotpSetupSuccess = async () => {
        await fetchMfaMethods();
    };

    const handleCloseTotpModal = () => {
        setTotpSetupModal({ open: false, otpauthUrl: null });
    };

    const handleRemoveMfa = async (mfaPk) => {
        setLoadingMfa(true);
        try {
            const res = await fetchData(`/person/my_profile/mfa/${mfaPk}`, "DELETE", true);
            if (res.status === HTTP_STATUS.OK_200) {
                Notifications.toast(
                    intl.get("MFARemoved").d("Authentication method removed successfully"),
                    Notifications.TYPE_SUCCESS
                );
                await fetchMfaMethods();
            } else {
                Notifications.toast(
                    res.data?.detail || intl.get("MFARemoveFailed").d("Failed to remove authentication method"),
                    Notifications.TYPE_ERROR
                );
            }
            return res;
        } catch (err) {
            console.error("Error removing MFA:", err);
            Notifications.toast(
                intl.get("MFARemoveError").d("Error removing authentication method. Please try again."),
                Notifications.TYPE_ERROR
            );
            return { status: HTTP_STATUS.INTERNAL_SERVER_ERROR_500 };
        } finally {
            setLoadingMfa(false);
        }
    };

    const handleSubmit = async (updateData) => {
        try {
            const phone = updateData.phone ? updateData.phone.replace(/\D/g, "") : "";
            const hasPerson = profileData.person || false;
            const currentEmail = profileData.dms_user?.user_email || "";
            const newEmail = updateData.email || "";
            const emailChanged = newEmail.toLowerCase() !== currentEmail.toLowerCase();

            const payload = {
                user_name: updateData.name,
                user_email: newEmail,
                user_phone: phone,
                user_language: updateData.user_language || profileData.dms_user?.user_language || systemLanguage,
            };

            if (hasPerson && profileData.person?.address) {
                payload.zip_code = updateData.zip_code;
                payload.city = updateData.city;
                payload.state = updateData.state;
                payload.address = updateData.address;
                payload.number = updateData.number;
            }

            if (userType === "doctor") {
                payload.crm_state = updateData.crm_state;
            }

            if (userType === "pharmacist") {
                payload.crf_state = updateData.crf_state;
            }

            const res = await fetchData("/person/my_profile", "PUT", true, payload);

            if (res.status === HTTP_STATUS.OK_200) {
                if (emailChanged) {
                    localStorage.clear();
                    sessionStorage.clear();
                    setTimeout(() => navigate(0), 200);
                    return res;
                }
                const refreshResponse = await fetchData("/person/my_profile", "GET", true);
                if (refreshResponse.status === HTTP_STATUS.OK_200) {
                    setProfileData(refreshResponse.data.data || refreshResponse.data);
                }
            }

            return res;
        } catch (err) {
            console.error("Error updating profile:", err);
            return { status: HTTP_STATUS.INTERNAL_SERVER_ERROR_500 };
        }
    };

    if (loading) {
        return (
            <DmsPage name={intl.get("ProfileSettings").d("Profile Settings")}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </DmsPage>
        );
    }

    if (error || !profileData) {
        return (
            <DmsPage name={intl.get("ProfileSettings").d("Profile Settings")}>
                <Alert severity="error">{error || intl.get("ProfileNotFound").d("Profile not found")}</Alert>
            </DmsPage>
        );
    }

    const hasPerson = profileData.person || false;

    if (!profileData.dms_user && !hasPerson) {
        return (
            <DmsPage name={intl.get("ProfileSettings").d("Profile Settings")}>
                <Alert severity="error">{intl.get("InvalidProfileData").d("Invalid profile data structure")}</Alert>
            </DmsPage>
        );
    }

    const tabs = [
        {
            name: 'profile',
            title: intl.get("ProfileInformation").d("Profile Information"),
            component: <ProfileInformationTab profileData={profileData} userType={userType} onSubmit={handleSubmit} />
        },
        {
            name: 'password',
            title: intl.get("ChangePassword").d("Change Password"),
            component: <ChangePasswordTab onSubmit={handlePasswordChange} />
        }
    ];

    // Institution tab for doctors, pharmacists, and institutional responsibles
    if (userType === 'doctor' || userType === 'pharmacist' || userType === 'institution_responsible' || userType === 'distributor_responsible') {
        tabs.push({
            name: 'institution',
            title: intl.get("Institution").d("Institution"),
            component: <InstitutionTab userType={userType} userTypes={userTypes} />
        });
    }

    // Documents tab for pharmacists and institutional responsibles to upload proof documents
    if (userType === 'pharmacist' || userType === 'institution_responsible' || userType === 'distributor_responsible') {
        tabs.push({
            name: 'documents',
            title: intl.get("Documents").d("Documents"),
            component: <DocumentsTab key={documentsTabKey} userType={userType} context="profile" onStatusChange={() => { fetchProfile(); setDocumentsTabKey(k => k + 1); }} />
        });
    }

    // User Types tab for managing professional profiles
    if (hasPerson || userType === 'person' || userType === 'doctor' || userType === 'pharmacist' || userType === 'institution_responsible' || userType === 'distributor_responsible') {
        tabs.push({
            name: 'profiles',
            title: intl.get("ProfessionalProfiles").d("Perfis Profissionais"),
            component: <UserTypesTab />
        });
    }

    // MFA tab should be shown for all users with person profile (doctor, pharmacist, institution_responsible)
    if (hasPerson || userType === 'person' || userType === 'doctor' || userType === 'pharmacist' || userType === 'institution_responsible' || userType === 'distributor_responsible') {
        tabs.push({
            name: 'security',
            title: intl.get("Security").d("Security"),
            component: <SecurityTab mfaMethods={mfaMethods} loadingMfa={loadingMfa} onAddMfa={handleAddMfa} onRemoveMfa={handleRemoveMfa} />
        });
    }

    return (
        <DmsPage name={intl.get("ProfileSettings").d("Profile Settings")}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h3" sx={{ fontSize: '1.1rem' }}>
                        {intl.get("ProfileSettings").d("Profile Settings")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {intl.get("UserType").d("User Type")}: <strong>
                                {userType === "regular_user"
                                    ? intl.get("RegularUser").d("Regular User")
                                    : userType === "person"
                                        ? intl.get("Person").d("Person")
                                        : userType
                                            ? getUserTypeLabel(mapBackendTypeToFrontend(userType))
                                            : "-"}
                            </strong>
                        </Typography>
                    </Box>
                    <DmsTabber tabs={tabs} pageName={intl.get("ProfileSettings").d("Profile Settings")} selected={tabParam ? Math.max(0, tabs.findIndex(t => t.name === tabParam)) : 0} />
                </DmsAltBox.Body>
            </DmsAltBox.Card>
            <TOTPSetupModal
                isVisible={totpSetupModal.open}
                onClose={handleCloseTotpModal}
                otpauthUrl={totpSetupModal.otpauthUrl}
                onSuccess={handleTotpSetupSuccess}
            />
        </DmsPage>
    );
};
